/**
 * The custom quiz review (GET …/activity/quizzes/<session_id>;
 * student_insights/types.py QuizReviewResponse) in the shape the paper
 * review's components draw (PaperReview's PartBody, PaperReviewQuestion), so
 * a quiz question is drawn with the same renderer and sanitiser as a paper
 * question.
 *
 * Every model-written string (passage, stem, options, explanation, steps)
 * is handed on as it came, to be drawn only through prepareSittingHtml. The
 * student's own written answer stays in student_answer.text, which the card
 * draws as text.
 */
import { dayMonthYear } from './activityFormat';

/** "Student's answer: A · Correct answer: B", for a question whose options were not saved. */
function answerNote(question) {
  const parts = [];
  if (question.student_answer?.letter) parts.push(`Student's answer: ${question.student_answer.letter}`);
  if (question.correct_letter) parts.push(`Correct answer: ${question.correct_letter}`);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * One quiz question as a paper review question. A written ("text")
 * question has no options; it is drawn as a multiple choice card without
 * any, which shows the student's answer as text.
 */
export function reviewQuestionFromQuiz(question) {
  const saved = question.text != null;
  return {
    number: question.number,
    topic: question.topic,
    kind: 'multiple_choice',
    text: question.text,
    image_url: null,
    options: (question.options || []).map((option) => ({
      letter: option.letter,
      text: option.text,
      is_correct: option.is_correct,
      image_url: null,
    })),
    student_answer: question.student_answer,
    outcome: question.outcome,
    explanation: question.explanation,
    time_spent_seconds: null,
    exhibit: null,
    passage: question.passage ? { text: question.passage, is_html: false } : null,
    steps: question.steps || [],
    answer_note: saved ? null : answerNote(question),
  };
}

/** The review's summary as PartBody reads it. Nothing is unrecorded, and there is no weakest topic. */
export const reviewSummaryFromQuiz = (summary) => ({
  correct: summary.correct,
  wrong: summary.wrong,
  unanswered: summary.unanswered,
  not_marked: summary.not_marked,
  not_recorded: 0,
});

/** The whole review, for PartBody: { summary, questions }. */
export const reviewFromQuiz = (response) => ({
  summary: reviewSummaryFromQuiz(response.summary),
  questions: (response.questions || []).map(reviewQuestionFromQuiz),
});

/**
 * Which note a review carries: 'app' (a pathbuddy.app quiz: totals only),
 * 'older' (made before questions were saved: topics and results only), or
 * null (the questions are shown).
 */
export function quizReviewKind(quiz) {
  if (quiz.source === 'app') return 'app';
  if (!quiz.questions_saved) return 'older';
  return null;
}

/**
 * The older-quiz note, dated by `savedSince` (the list's
 * questions_saved_since: when the platform's first quiz was saved). Without
 * a date (none saved yet, or it could not be loaded) it says "older quizzes".
 */
export function olderQuizNote(savedSince) {
  const date = dayMonthYear(savedSince);
  return date
    ? `Questions weren't saved for quizzes made before ${date}. Topics and results are shown.`
    : "Questions weren't saved for older quizzes. Topics and results are shown.";
}

export const APP_QUIZ_NOTE = 'This quiz was made on pathbuddy.app, which records only its totals. Its questions are not shown.';
